/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  ActionIcon,
  Card, DetailStack, Dropdown, HStack, ICON_EYE_OUTLINE, Popover, Stack, Text,
} from '@doveit/bricks';
import { Task } from '../../../providers/api/dtos';
import TaskPreview from '../../components/task-preview/TaskPreview';
import * as styles from './ViewTaskCard.style';
import TaskPriorityBadge from '../../components/task-priority-badge/TaskPriorityBadge';
import { CELL_DATE_WIDTH } from '../../../constants';
import DateViewer from '../../../components/date-viewer/DateViewer';
import { ReferenceType, TaskStatus } from '../../../domain/types';
import { goToDetailPage } from '../../../utils/navigate/utils';
import UpdateTaskStatus from '../update-task-status/UpdateTaskStatus';
import UpsertTaskAction from '../upsert-task-action/UpsertTaskAction';
import { contentEditorUsers } from '../../constants';
import UserPopoverPreview from '../../../user/components/user-popover-preview/UserPopoverPreview';
import UserAvatar from '../../../user/components/user-avatar/UserAvatar';

export interface ViewTaskCardProps extends React.AriaAttributes {
  task: Task,
  onTaskUpdated?: (task: Task) => void,
}

const ViewTaskCard: React.FC<ViewTaskCardProps> = ({
  task,
  onTaskUpdated,
  ...rest
}) => {
  const goToTaskPage = React.useCallback(() => {
    goToDetailPage(task.id!, ReferenceType.TASK);
  }, [task.id]);

  /**
    * TODO: https://doveit.atlassian.net/browse/DOVE-14128
    */
  const assignee = React.useMemo(
    () => (task?.assignee ? contentEditorUsers.find((u) => u.email === task.assignee) : undefined),
    [task],
  );

  return (
    <Card {...rest}>
      <Card.Header
        primaryActions={[
          <ActionIcon
            aria-label="Visualizza attività"
            label="Visualizza attività"
            size="S"
            icon={{ path: ICON_EYE_OUTLINE }}
            onClick={goToTaskPage}
          />,
        ]}
        secondaryActions={task?.status !== TaskStatus.DONE ? [
          <UpsertTaskAction
            onSuccess={onTaskUpdated}
            task={task}
          >
            {({ upsert, label }) => (
              <Dropdown.Option
                label={label}
                aria-label={`${label} attività`}
                onClick={upsert}
              />
            )}
          </UpsertTaskAction>,
        ] : []}
      >
        <TaskPreview task={task} />
      </Card.Header>
      <Card.Content aria-label="Informazioni attivita">
        <Stack gap={150}>
          <HStack gap={100}>
            <UpdateTaskStatus
              task={task}
              onSuccess={onTaskUpdated}
              size="XS"
            />
            <TaskPriorityBadge
              task={task}
              size="XS"
            />
          </HStack>
          <Stack gap={150}>
            <HStack
              gap={100}
              wrap="nowrap"
              justifyContent="space-between"
            >
              <DetailStack
                label="Aggiunta"
                aria-label="Data di creazione attività"
                width={CELL_DATE_WIDTH}
              >
                <DateViewer
                  stringDate={task.createdAt!}
                  checkIfToday
                />
              </DetailStack>

              <UserPopoverPreview
                user={assignee}
                trigger={<UserAvatar size="S" />}
                namePlaceholder="Non assegnato"
              />
            </HStack>

            {task.description && (
              <Popover
                aria-label="Descrizione attività"
                trigger={(
                  <Text.Caption css={styles.description}>
                    {task.description}
                  </Text.Caption>
                )}
                hasArrow
              >
                <div css={styles.descriptionPopoverWrapper}>
                  <Text.Body>
                    {task.description}
                  </Text.Body>
                </div>
              </Popover>
            )}
          </Stack>
        </Stack>
      </Card.Content>
    </Card>
  );
};

export default ViewTaskCard;
